import { useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Empty, Flex, Image, Input, Modal, Segmented, Select, Skeleton, Space, Tag, Tooltip, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, type Me } from '../../app/api-context';
import { useSession } from '../../app/session-context';
import { CalibrationNotice, PhysicalImage } from '../../components/PhysicalSize';
import { STAGE_LABEL, won } from '../../lib/generation-types';
import { Link } from 'react-router';
import { ManualModal, type ManualSource } from '../images/ManualModal';
import { ContinuousReview } from './ContinuousReview';
import { QUEUE_LABEL, REVIEWABLE_STAGES, REWORK_PRESETS, STEP_LABEL, VERDICT_LABEL, EXISTING_KIND_LABEL, UNASSIGNED, canAssign, canPublishSearch, canReprocess, cdnUrl, groupByVocab, isFullBleed, isLegacyTransparent, type PublishResult, type QueueName, type ReviewAsset, type ReviewQueue, type Verdict, type VerdictResult } from '../../lib/review-types';
import { VocabHint, type VocabHintData } from '../../components/VocabHint';

type View = 'list' | '3' | '5';
type Mode = 'group' | 'continuous';
type Source = 'final' | 'original';
const APPROVE: Verdict[] = ['default', 'search', 'both'];
const CHECKER = 'repeating-conic-gradient(#f3f3f3 0% 25%, #fff 0% 50%) 50% / 16px 16px';

function PublishInfo({ a, name, canPublish, onPublish, busy, slug }: { a: ReviewAsset; name: string; canPublish: boolean; onPublish: () => void; busy: boolean; slug: string | null }) {
  const p = a.publish?.search;
  return (
    <div style={{ marginTop: 6, fontSize: 12 }}>
      {p && (
        <>
          {p.state === 'succeeded' ? (
            <Typography.Text type="success" style={{ fontSize: 12, display: 'block' }}>검색용 게시 완료 · {p.searchImageId}</Typography.Text>
          ) : (
            (Object.keys(STEP_LABEL) as (keyof typeof STEP_LABEL)[]).map((k) => {
              const st = p.steps[k];
              return st.state === 'failed' ? (
                <Typography.Text key={k} type="danger" style={{ fontSize: 12, display: 'block' }}>{STEP_LABEL[k]} 실패: {st.error}</Typography.Text>
              ) : st.state === 'succeeded' ? (
                <Typography.Text key={k} style={{ fontSize: 12, display: 'block' }}>✓ {STEP_LABEL[k]}</Typography.Text>
              ) : null;
            })
          )}
          {p.steps.upload.state === 'succeeded' && <a href={cdnUrl(p)} target="_blank" rel="noreferrer">CDN 이미지</a>}
          {p.state === 'succeeded' && <> · <Link to={`/search?q=${encodeURIComponent(name)}`}>앱 검색 확인</Link></>}
        </>
      )}
      {canPublish && canPublishSearch(a) && (
        slug ? (
          <Button size="small" type="primary" style={{ marginTop: 4, display: 'block' }} loading={busy} onClick={onPublish}>
            {p && p.state !== 'running' ? '검색용 게시 다시 시도' : '검색용 게시'}
          </Button>
        ) : (
          <Typography.Text type="warning" style={{ fontSize: 12, display: 'block' }}>
            게시하려면 <Link to="/vocab">어휘 관리</Link>에서 영문 파일명(슬러그)을 입력하세요.
          </Typography.Text>
        )
      )}
    </div>
  );
}

function AssetTile({ a, view, source, name, canJudge, checked, onCheck, onVerdict, busy, onManual, slug, onPublish, publishing, onReprocess, reprocessing, vocabOptions, onAssign }: {
  a: ReviewAsset; view: View; source: Source; name: string; canJudge: boolean; checked: boolean; busy: boolean;
  onCheck: (v: boolean) => void; onVerdict: (v: Verdict) => void; onManual: () => void; onReprocess: () => void; reprocessing: boolean; slug: string | null; onPublish: () => void; publishing: boolean;
  vocabOptions: { value: string; label: string }[]; onAssign: (vocabId: string) => void;
}) {
  const src = (source === 'final' ? a.finalUrl : a.originalUrl) ?? a.originalUrl ?? a.finalUrl;
  const stage = STAGE_LABEL[a.stage] ?? { label: a.stage, color: 'default' };
  const reviewable = canJudge && REVIEWABLE_STAGES.has(a.stage);
  return (
    <Card size="small" style={{ width: view === 'list' ? 200 : 'auto' }} styles={{ body: { padding: 8 } }} data-testid={`asset-${a.assetId}`}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
        {reviewable ? <Checkbox checked={checked} onChange={(e) => onCheck(e.target.checked)} aria-label={`${name} 선택`} /> : <span />}
        <Tag color={stage.color} style={{ marginInlineEnd: 0 }}>{stage.label}</Tag>
      </Flex>
      {src ? (
        view === 'list' ? <Image src={src} width={182} height={182} style={{ objectFit: 'contain', background: CHECKER }} alt={name} /> : <PhysicalImage src={src} alt={name} size={view === '3' ? 3 : 5} />
      ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="이미지 없음" />}
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
        {a.origin === 'manual' ? '수동 제작 · ' : a.parentAssetId ? '투명 재생성 · ' : ''}{a.generation?.modelId.replace('openai/', '') ?? '-'}{a.generation?.costKrw != null ? ` · ${won(a.generation.costKrw)}` : ''}
      </Typography.Text>
      {a.postprocess?.state === 'failed' && <Typography.Text type="warning" style={{ fontSize: 12, display: 'block' }}>{a.postprocess.reasons[0] ?? '후처리 실패'}</Typography.Text>}
      {isFullBleed(a) && <Tooltip title="배경을 판정할 수 없어(귀·코 클로즈업처럼 피사체가 화면을 가득 채우거나 장면 전체) 흰 배경 처리 없이 512 전체를 썼습니다. 그대로 써도 되는지 확인해 주세요."><Tag color="purple" style={{ marginTop: 4 }}>배경 없음(가득 찬 피사체)</Tag></Tooltip>}
      {isLegacyTransparent(a) && <Tooltip title="흰 배경 규격(2026-11-01) 이전에 만든 투명 배경 최종본입니다. '후처리 다시 하기'로 원본에서 흰 배경으로 다시 만들 수 있습니다(비용 없음)."><Tag color="gold" style={{ marginTop: 4 }}>이전 규격(투명 배경)</Tag></Tooltip>}
      {a.review.verdict && (
        <Tooltip title={a.review.history.map((h) => `${new Date(h.at).toLocaleString('ko-KR')} ${VERDICT_LABEL[h.verdict]}${h.note ? `: ${h.note}` : ''} (${h.by})`).join('\n')}>
          <Typography.Text style={{ fontSize: 12, display: 'block' }}>판정 {VERDICT_LABEL[a.review.verdict]}{a.review.note ? ` · ${a.review.note}` : ''}{a.review.history.length > 1 ? ` (수정 ${a.review.history.length - 1}회)` : ''}</Typography.Text>
        </Tooltip>
      )}
      {a.origin === 'existing' && a.existing && (
        <Tooltip title={a.existing.url}><Tag color="cyan" style={{ marginTop: 4 }}>{EXISTING_KIND_LABEL[a.existing.kind]}</Tag></Tooltip>
      )}
      {canJudge && canAssign(a) && (
        <Select
          size="small"
          showSearch
          optionFilterProp="label"
          placeholder={a.vocabIds.length ? '연결 어휘 바꾸기' : '어휘 연결(검색용 게시에 필요)'}
          aria-label={`${name} 어휘 연결`}
          value={a.vocabIds[0] ?? null}
          onChange={onAssign}
          options={vocabOptions}
          style={{ width: '100%', marginTop: 4 }}
        />
      )}
      {canJudge && a.origin === 'existing' && a.stage === 'published' && (
        <Tooltip title="교체가 필요한 기존 이미지로 표시합니다. 운영 파일·검색 데이터는 바뀌지 않습니다.">
          <Button size="small" disabled={busy} style={{ marginTop: 4, display: 'block' }} onClick={() => onVerdict('rework')}>재작업 표시</Button>
        </Tooltip>
      )}
      {canJudge && a.vocabIds.length > 0 && <Button size="small" type="link" style={{ padding: 0, marginTop: 4 }} onClick={onManual}>{a.origin === 'existing' ? '참고해 새로 만들기(수동 제작)' : '수동 제작(프롬프트 고쳐 다시 만들기)'}</Button>}
      {canJudge && canReprocess(a) && <Button size="small" type="link" style={{ padding: 0, display: 'block' }} loading={reprocessing} onClick={onReprocess}>후처리 다시 하기(비용 없음)</Button>}
      <PublishInfo a={a} name={name} canPublish={canJudge} onPublish={onPublish} busy={publishing} slug={slug} />
      {reviewable && (
        <Flex wrap gap={4} style={{ marginTop: 6 }}>
          {APPROVE.map((v) => <Button key={v} size="small" disabled={busy} type={a.review.verdict === v ? 'primary' : 'default'} onClick={() => onVerdict(v)}>{VERDICT_LABEL[v]}</Button>)}
          <Button size="small" disabled={busy} onClick={() => onVerdict('rework')}>재작업</Button>
          <Button size="small" danger disabled={busy} onClick={() => onVerdict('discard')}>폐기</Button>
        </Flex>
      )}
    </Card>
  );
}

export function ReviewPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useSession();
  const [msg, holder] = message.useMessage();
  const [queue, setQueue] = useState<QueueName>('pending');
  const [view, setView] = useState<View>('list');
  const [source, setSource] = useState<Source>('final');
  const [selected, setSelected] = useState<string[]>([]);
  const [rework, setRework] = useState<{ ids: string[]; note: string } | null>(null);
  const [manual, setManual] = useState<ManualSource | null>(null);
  const [mode, setMode] = useState<Mode>('group');
  const me = useQuery({ queryKey: ['me', user?.email ?? null], queryFn: () => api.get<Me>('/me'), staleTime: 5 * 60_000, retry: false });
  const data = useQuery({ queryKey: ['review', queue], queryFn: () => api.get<ReviewQueue>(`/review/queue?queue=${queue}`), retry: false });
  const canJudge = me.data?.role === 'editor';

  const judge = useMutation({
    mutationFn: (b: { assetIds: string[]; verdict: Verdict; note?: string }) =>
      // 3cm 보기·연속 검수(3cm를 함께 보여줌)에서 판정하면 3cm 인지 여부를 함께 남긴다(승인 = 인지 가능, 재작업·폐기 = 어려움)
      api.post<VerdictResult>('/review/verdict', { ...b, recognizableAt3cm: view === '3' || mode === 'continuous' ? APPROVE.includes(b.verdict) : null }),
    onSuccess: (r, b) => {
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length) msg.warning(`${r.okCount}건 판정, ${failed.length}건 실패: ${failed.map((f) => f.error).join(' / ')}`);
      else msg.success(`${r.okCount}건을 '${VERDICT_LABEL[b.verdict]}'(으)로 판정했습니다.`);
      setSelected((s) => s.filter((id) => !r.results.some((x) => x.ok && x.assetId === id)));
      setRework(null);
      void qc.invalidateQueries({ queryKey: ['review'] });
      void qc.invalidateQueries({ queryKey: ['vocab'] });
    },
    onError: (e) => msg.error((e as Error).message),
  });
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const publish = useMutation({
    mutationFn: (assetId: string) => {
      setPublishingId(assetId);
      return api.post<PublishResult>('/publish/search', { assetId });
    },
    onSuccess: (r) => {
      if (r.state === 'succeeded') msg.success(r.already ? '이미 게시된 이미지입니다.' : `검색용으로 게시했습니다(${r.searchImageId}). 앱 검색에 바로 나타납니다.`);
      else msg.error(`게시가 끝나지 않았습니다(${r.failedStep ?? '-'}): ${r.error ?? ''}. 원인을 확인한 뒤 다시 시도하세요.`);
    },
    onError: (e) => msg.error((e as Error).message),
    onSettled: () => {
      setPublishingId(null);
      void qc.invalidateQueries({ queryKey: ['review'] });
      void qc.invalidateQueries({ queryKey: ['vocab'] });
    },
  });
  const [reprocessingIds, setReprocessingIds] = useState<string[]>([]);
  const reprocess = useMutation({
    mutationFn: (assetIds: string[]) => {
      setReprocessingIds(assetIds);
      return api.post<VerdictResult>('/generation/reprocess', { assetIds });
    },
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length) msg.warning(`${r.okCount}건 다시 처리, ${failed.length}건 실패: ${failed.map((f) => f.error).join(' / ')}`);
      else msg.success(`${r.okCount}건을 흰 배경 규격으로 다시 처리했습니다.`);
    },
    onError: (e) => msg.error((e as Error).message),
    onSettled: () => {
      setReprocessingIds([]);
      void qc.invalidateQueries({ queryKey: ['review'] });
      void qc.invalidateQueries({ queryKey: ['vocab'] });
    },
  });
  const verdict = (ids: string[], v: Verdict) => (v === 'rework' ? setRework({ ids, note: '' }) : judge.mutate({ assetIds: ids, verdict: v }));

  const names = useMemo(() => new Map((data.data?.vocab ?? []).map((v) => [v.vocabId, v.text])), [data.data]);
  const slugs = useMemo(() => new Map((data.data?.vocab ?? []).map((v) => [v.vocabId, v.slug])), [data.data]);
  const hints = useMemo(() => new Map<string, VocabHintData>((data.data?.vocab ?? []).map((v) => [v.vocabId, { slug: v.slug, keywords: v.keywords }])), [data.data]);
  const groups = groupByVocab(data.data?.assets ?? []);
  // 앱 기존 이미지 어휘 연결(#75)
  const vocabList = useQuery({ queryKey: ['vocab', 'list'], queryFn: () => api.get<{ items: { vocabId: string; text: string; slug: string | null }[] }>('/vocab'), enabled: canJudge, retry: false });
  const vocabOptions = useMemo(() => (vocabList.data?.items ?? []).map((v) => ({ value: v.vocabId, label: `${v.text}${v.slug ? ` · ${v.slug}` : ''} (${v.vocabId})` })), [vocabList.data]);
  const assign = useMutation({
    mutationFn: (b: { assetId: string; vocabId: string }) => api.post<{ vocabIds: string[] }>('/review/assign', b),
    onSuccess: () => {
      msg.success('어휘를 연결했습니다.');
      void qc.invalidateQueries({ queryKey: ['review'] });
      void qc.invalidateQueries({ queryKey: ['vocab'] });
    },
    onError: (e) => msg.error((e as Error).message),
  });
  const importUnused = useMutation({
    mutationFn: () => api.post<{ created: number; total: number; remaining: number; results: { outcome: string; error?: string }[] }>('/review/import-unused', {}),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => x.outcome === 'failed');
      const text = `사용하지 않는 운영 이미지 ${r.total}개 중 ${r.created}개를 검수 대기로 불러왔습니다${r.remaining ? `(남은 ${r.remaining}개는 다시 누르세요)` : ''}.`;
      if (failed.length) msg.warning(`${text} 실패 ${failed.length}개: ${failed.map((f) => f.error).join(' / ')}`);
      else msg.success(text);
      void qc.invalidateQueries({ queryKey: ['review'] });
    },
    onError: (e) => msg.error((e as Error).message),
  });

  return (
    <>
      {holder}
      <Typography.Title level={3}>검수함</Typography.Title>
      <Typography.Paragraph type="secondary">
        같은 어휘의 후보를 나란히 비교해 판정합니다. 의미 전달력과 3cm에서의 인지 가능성은 관리자가 판단합니다. 승인과 게시는 별개입니다. 검색용으로 승인한 이미지는 '승인(미게시)'에서 게시하면 앱 검색에 나타납니다.
      </Typography.Paragraph>

      <Flex wrap gap={8} align="center" style={{ marginBottom: 12 }}>
        <Segmented<QueueName> value={queue} onChange={(q) => { setQueue(q); setSelected([]); }} options={(Object.keys(QUEUE_LABEL) as QueueName[]).map((q) => ({ value: q, label: QUEUE_LABEL[q] }))} />
        {mode === 'group' && <Segmented<View> value={view} onChange={setView} options={[{ value: 'list', label: '목록' }, { value: '3', label: '3cm' }, { value: '5', label: '5cm' }]} />}
        {mode === 'group' && <Segmented<Source> value={source} onChange={setSource} options={[{ value: 'final', label: '최종 512' }, { value: 'original', label: '원본' }]} />}
        {canJudge && <Segmented<Mode> value={mode} onChange={(m) => { setMode(m); setSelected([]); }} options={[{ value: 'group', label: '어휘별 묶음' }, { value: 'continuous', label: '연속 검수(키보드)' }]} />}
        {canJudge && (
          <Tooltip title="현재 릴리스·검색 이미지 어디서도 쓰지 않는 운영 이미지를 어휘 없이 검수 대기로 불러옵니다. 운영 파일은 바꾸지 않습니다(비용 없음).">
            <Button size="small" loading={importUnused.isPending} onClick={() => importUnused.mutate()} style={{ marginInlineStart: 'auto' }}>사용하지 않는 운영 이미지 불러오기</Button>
          </Tooltip>
        )}
      </Flex>
      {(view !== 'list' || mode === 'continuous') && <div style={{ marginBottom: 12 }}><CalibrationNotice /></div>}

      {mode === 'group' && canJudge && selected.length > 0 && (
        <Card size="small" style={{ marginBottom: 12, position: 'sticky', top: 0, zIndex: 2 }}>
          <Flex wrap gap={8} align="center">
            <Typography.Text strong>선택 {selected.length}개</Typography.Text>
            {APPROVE.map((v) => <Button key={v} disabled={judge.isPending} onClick={() => verdict(selected, v)}>{VERDICT_LABEL[v]}</Button>)}
            <Button disabled={judge.isPending} onClick={() => verdict(selected, 'rework')}>재작업</Button>
            <Button danger disabled={judge.isPending} onClick={() => verdict(selected, 'discard')}>폐기</Button>
            <Button disabled={reprocess.isPending || selected.length > 20} onClick={() => reprocess.mutate(selected)}>후처리 다시 하기</Button>
            <Button type="link" onClick={() => setSelected([])}>선택 해제</Button>
          </Flex>
        </Card>
      )}

      {data.isPending && <Skeleton active />}
      {data.isError && <Alert type="error" showIcon message="검수 목록을 불러오지 못했습니다" description={(data.error as Error).message} />}
      {data.data && !groups.length && <Empty description={`${QUEUE_LABEL[queue]} 이미지가 없습니다`} />}
      {mode === 'continuous' && data.data && (
        <ContinuousReview
          assets={(data.data.assets ?? []).filter((a) => REVIEWABLE_STAGES.has(a.stage))}
          names={names}
          hints={hints}
          busy={judge.isPending}
          paused={!!rework || !!manual}
          onVerdict={verdict}
        />
      )}
      {mode === 'group' && (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {groups.map((g) => (
          <div key={g.vocabId}>
            <Typography.Title level={5} style={{ margin: '0 0 8px' }}>
              {g.vocabId === UNASSIGNED ? '어휘 미연결(사용하지 않는 운영 이미지)' : names.get(g.vocabId) ?? g.vocabId} <VocabHint hint={hints.get(g.vocabId)} /> <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal' }}>{g.vocabId === UNASSIGNED ? `폐기·재작업하거나 어휘를 연결해 검색용으로 게시하세요 · ${g.items.length}장` : `${g.vocabId} · 후보 ${g.items.length}`}</Typography.Text>
            </Typography.Title>
            <Flex wrap gap={12} align="flex-start">
              {g.items.map((a) => (
                <AssetTile
                  key={a.assetId}
                  a={a}
                  view={view}
                  source={source}
                  name={names.get(g.vocabId) ?? g.vocabId}
                  canJudge={canJudge}
                  busy={judge.isPending}
                  checked={selected.includes(a.assetId)}
                  onCheck={(c) => setSelected((s) => (c ? [...s, a.assetId] : s.filter((x) => x !== a.assetId)))}
                  onVerdict={(v) => verdict([a.assetId], v)}
                  onManual={() => setManual({ vocabId: g.vocabId, text: names.get(g.vocabId) ?? g.vocabId, asset: { assetId: a.assetId, prompt: a.generation?.prompt ?? null, promptParts: a.generation?.promptParts ?? null, modelId: a.generation?.modelId ?? null, previewUrl: a.finalUrl ?? a.originalUrl, hasOriginal: !!a.originalUrl, preferReference: a.origin === 'existing' } })}
                  slug={slugs.get(g.vocabId) ?? null}
                  publishing={publishingId === a.assetId}
                  onPublish={() => publish.mutate(a.assetId)}
                  onReprocess={() => reprocess.mutate([a.assetId])}
                  vocabOptions={vocabOptions}
                  onAssign={(vocabId) => assign.mutate({ assetId: a.assetId, vocabId })}
                  reprocessing={reprocessingIds.includes(a.assetId)}
                />
              ))}
            </Flex>
          </div>
        ))}
      </Space>
      )}

      <ManualModal source={manual} onClose={() => setManual(null)} />
      <Modal
        open={!!rework}
        title={`재작업 사유 (${rework?.ids.length ?? 0}개)`}
        okText="재작업으로 판정"
        cancelText="취소"
        okButtonProps={{ disabled: !rework?.note.trim() }}
        confirmLoading={judge.isPending}
        onCancel={() => setRework(null)}
        onOk={() => rework && judge.mutate({ assetIds: rework.ids, verdict: 'rework', note: rework.note.trim() })}
        destroyOnHidden
      >
        <Flex wrap gap={6} style={{ marginBottom: 8 }}>
          {REWORK_PRESETS.map((p) => <Tag.CheckableTag key={p} checked={rework?.note === p} onChange={() => setRework((r) => (r ? { ...r, note: p } : r))}>{p}</Tag.CheckableTag>)}
        </Flex>
        <Input.TextArea placeholder="사유를 고르거나 직접 입력" value={rework?.note ?? ''} maxLength={1000} autoSize={{ minRows: 2 }} onChange={(e) => setRework((r) => (r ? { ...r, note: e.target.value } : r))} />
      </Modal>
    </>
  );
}
